import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ChevronRight, Download, File as FileIcon, Folder, Play } from "lucide-react";
import { getSharedFolder, type SharedFolderFile } from "@/lib/videos.functions";

export const Route = createFileRoute("/media/$token")({
  component: SharedMediaPage,
  head: () => ({
    meta: [
      { title: "Gedeelde media | Casa Mi Pajobe" },
      { name: "description", content: "Bekijk en download de gedeelde bestanden." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Gedeelde media | Casa Mi Pajobe" },
      { property: "og:description", content: "Bekijk en download de gedeelde bestanden." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function formatSize(bytes: number | null) {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
}

function SharedMediaPage() {
  const { token } = Route.useParams();
  const fetchFolder = useServerFn(getSharedFolder);
  const [subPath, setSubPath] = useState("");
  const [preview, setPreview] = useState<SharedFolderFile | null>(null);

  const q = useQuery({
    queryKey: ["shared-folder", token, subPath],
    queryFn: () => fetchFolder({ data: { token, subPath } }),
  });

  const segments = subPath ? subPath.split("/") : [];

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <h1 className="font-display text-2xl font-semibold">{q.data?.rootName ?? "Gedeelde media"}</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Je kan deze bestanden bekijken en downloaden in originele kwaliteit.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
        <button onClick={() => setSubPath("")} className="hover:underline">
          {q.data?.rootName ?? "Start"}
        </button>
        {segments.map((seg, i) => (
          <span key={`${seg}-${i}`} className="flex items-center gap-1">
            <ChevronRight className="h-3.5 w-3.5" />
            <button
              onClick={() => setSubPath(segments.slice(0, i + 1).join("/"))}
              className="hover:underline"
            >
              {seg}
            </button>
          </span>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-border/70 bg-card">
        {q.isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Laden…</div>
        ) : q.error ? (
          <div className="p-8 text-center text-sm text-destructive">
            {(q.error as Error).message}
          </div>
        ) : (q.data?.folders.length ?? 0) === 0 && (q.data?.files.length ?? 0) === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Deze map is leeg.</div>
        ) : (
          <ul className="divide-y divide-border/70">
            {q.data?.folders.map((f) => (
              <li key={f.path}>
                <button
                  onClick={() => setSubPath(f.path)}
                  className="flex w-full items-center gap-3 p-4 text-left hover:bg-muted/50"
                >
                  <Folder className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate font-medium">{f.name}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            ))}
            {q.data?.files.map((f) => (
              <li key={f.path} className="flex flex-wrap items-center gap-3 p-4">
                <FileIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{f.name}</div>
                  <div className="text-xs text-muted-foreground">{formatSize(f.size)}</div>
                </div>
                {(f.mimeType ?? "").startsWith("video/") && (
                  <button
                    onClick={() => setPreview(f)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted"
                  >
                    <Play className="h-3.5 w-3.5" /> Bekijken
                  </button>
                )}
                <a
                  href={f.downloadUrl}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs text-primary-foreground"
                >
                  <Download className="h-3.5 w-3.5" /> Downloaden
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setPreview(null)}
        >
          <div
            className="w-full max-w-3xl rounded-2xl bg-card p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="truncate text-sm font-medium">{preview.name}</div>
              <button onClick={() => setPreview(null)} className="text-sm underline">
                Sluiten
              </button>
            </div>
            <video
              src={preview.viewUrl}
              controls
              playsInline
              className="max-h-[70vh] w-full rounded-lg bg-black"
            />
          </div>
        </div>
      )}
    </main>
  );
}
